import { z } from 'zod';
import { createInquirySchema, customerSchema, reminderWriteSchema, type CustomerMatchMode, type Inquiry } from '@worki/shared';

export const meetingDraftSchema = z.object({
  name: customerSchema.shape.name,
  phone: customerSchema.shape.phone,
  email: customerSchema.shape.email,
  subject: createInquirySchema.shape.subject.unwrap().min(1, 'validation.reminderTitle.required'),
  meetingAt: reminderWriteSchema.shape.dueAt,
  notes: createInquirySchema.shape.message,
});

export type MeetingDraft = z.infer<typeof meetingDraftSchema>;

/** Preserve the created inquiry before scheduling: a failed second request can safely retry just the booking. */
export async function scheduleMeeting(input: MeetingDraft, options: {
  createdId: string | null;
  customerMode?: CustomerMatchMode;
  onCreated: (id: string) => void;
  request: (method: 'post' | 'patch', path: string, body: unknown) => Promise<Inquiry>;
}): Promise<Inquiry> {
  const data = meetingDraftSchema.parse(input);
  let id = options.createdId;
  if (!id) {
    const inquiry = await options.request('post', '/inquiries', {
      customer: { name: data.name, phone: data.phone, email: data.email },
      customerMode: options.customerMode ?? 'auto',
      source: 'manual', subject: data.subject, message: data.notes,
    });
    id = inquiry.id;
    options.onCreated(id);
  }
  return options.request('patch', `/inquiries/${encodeURIComponent(id)}`, {
    status: 'meeting_scheduled', meetingAt: data.meetingAt, subject: data.subject, message: data.notes,
  });
}
